import qs from "qs"
import axios, { AxiosResponse } from "axios"
import axiosInstance from "@/api/axios"
import { ZodError } from "zod"
import errorTypeConst from "@common_lib/base/constants/errorTypeConst"
import extractLastSegment from "@/utils/extractLastSegment"

const REQUEST_CANCELED_MESSAGE_PREFIX = "request canceled for "

export const isCanceledRequestError = (error: unknown): boolean =>
  error instanceof Error && error.message.startsWith(REQUEST_CANCELED_MESSAGE_PREFIX)

export const ERROR_TYPE_CONST = {
  IP_BLOCK: "1002",
  IPS_BLOCK: "1009",
  INSUFFICIENT_PARAMETER: "3000"
}

export const fetchAPI = async ({
  data,
  method,
  url,
  qsOptions,
  withCredentials,
  hasStatus,
  ...rest
}: {
  data?: object
  method?: "post" | "get"
  url: string
  qsOptions?: object
  withCredentials?: boolean
  hasStatus?: boolean
  [key: string]: unknown
}) => {
  const queryPass = axiosInstance.defaults.headers.common.Authorization
  const formData = qs.stringify({ ...data }, qsOptions)
  const websiteInfoStore = useWebsiteInfoStore()

  try {
    const res = await axiosInstance({
      ...rest,
      withCredentials: withCredentials !== false,
      method: method ?? "post",
      data: formData,
      url: queryPass ? `${url};jsessionid=${queryPass}` : url
    })

    const { status } = res.data

    if (status === ERROR_TYPE_CONST.IP_BLOCK || status === ERROR_TYPE_CONST.IPS_BLOCK) {
      websiteInfoStore.updateWebsiteErrorStatus("IPBlock")
      return Promise.reject(res.data)
    }

    if (status === errorTypeConst.SYSTEM_MAINTENANCE.status) {
      websiteInfoStore.updateWebsiteErrorStatus("SystemMaintenance")
      return Promise.reject(res.data)
    }

    if (
      status === errorTypeConst.NOT_AUTHORIZED.status ||
      status === errorTypeConst.USER_IS_NULL.status ||
      status === errorTypeConst.SESSION_TIMEOUT.status ||
      (url === "/member/playerService/queryPlayerInfo" &&
        status === errorTypeConst.FAIL.status)
    ) {
      return Promise.reject(errorTypeConst.NOT_AUTHORIZED.status)
    }
    if (status === ERROR_TYPE_CONST.INSUFFICIENT_PARAMETER) {
      return Promise.reject(res.data)
    }
    if (status === errorTypeConst.CASINO_MAINTENANCE.status) {
      return Promise.reject(errorTypeConst.CASINO_MAINTENANCE.message)
    }
    if (res.data.error) {
      return Promise.reject(res.data)
    }
    if (hasStatus && res.data.status !== "0000") {
      return Promise.reject(res.data)
    }

    return Promise.resolve(res.data)
  } catch (error) {
    if (error instanceof ZodError) {
      throw new Error(`${error}, zod validate not pass`)
    }

    if (axios.isCancel(error)) {
      const message = `${REQUEST_CANCELED_MESSAGE_PREFIX}${extractLastSegment(url)}`
      console.warn(message)
      throw new Error(message)
    }

    const err = error as { response: AxiosResponse }

    if (
      err.response &&
      url === "/member/playerService/queryPlayerInfo" &&
      err.response.status === 410 &&
      err.response?.data?.status === errorTypeConst.FAIL.status
    ) {
      console.warn(err.response.data.status_msg)
      return Promise.reject(errorTypeConst.NOT_AUTHORIZED.status)
    }

    throw new Error(`${error}`)
  }
}

export default fetchAPI
